import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

export interface ProductQuadrantData {
  productId: string;
  sales: number;
  cost: number;
  color: string;
  imageUrl?: string;
}

interface QuadrantChartProps {
  data: ProductQuadrantData[];
  onProductHover?: (productId: string | null) => void;
  hoveredProductId?: string | null;
}

export const QuadrantChart: React.FC<QuadrantChartProps> = ({
  data,
  onProductHover,
  hoveredProductId = null
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Use fixed reference dimensions for consistent scaling
    const referenceHeight = 700;
    const referenceWidth = 790; // 700 * 1.2 for 1.2:1 aspect ratio

    // Set up dimensions and margins
    const margin = { top: 10, right: 60, bottom: 60, left: 60 };
    const innerWidth = referenceWidth - margin.left - margin.right;
    const innerHeight = referenceHeight - margin.top - margin.bottom;

    // Create main group
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Calculate median values for dividing quadrants
    const salesValues = data.map(d => d.sales);
    const costValues = data.map(d => d.cost);
    
    const medianSales = d3.median(salesValues) || 0;
    const medianCost = d3.median(costValues) || 0;

    // Set up scales with some padding
    const salesExtent = d3.extent(salesValues) as [number, number];
    const costExtent = d3.extent(costValues) as [number, number];
    
    const salesRange = salesExtent[1] - salesExtent[0];
    const costRange = costExtent[1] - costExtent[0];
    const salesPadding = salesRange === 0 ? Math.max(1, Math.abs(salesExtent[0]) * 0.15) : salesRange * 0.15;
    const costPadding = costRange === 0 ? Math.max(1, Math.abs(costExtent[0]) * 0.1) : costRange * 0.1;

    // Single continuous scales for positioning
    const xScale = d3.scaleLinear()
      .domain([costExtent[0] - costPadding, costExtent[1] + costPadding])
      .range([0, innerWidth]);

    const yScale = d3.scaleLinear()
      .domain([salesExtent[0] - salesPadding, salesExtent[1] + salesPadding])
      .range([innerHeight, 0]); // Inverted for SVG coordinates

    // Simple position functions
    const getXPosition = (cost: number) => xScale(cost);
    const getYPosition = (sales: number) => yScale(sales);

    // Calculate where the median lines should be drawn (for visual quadrants)
    const medianX = xScale(medianCost);
    const medianY = yScale(medianSales);
    
    // Visual quadrant dimensions (equal 1/4 of screen each)
    const halfWidth = innerWidth / 2;
    const halfHeight = innerHeight / 2;

    // Draw quadrant backgrounds with equal visual sizes (1/4 of screen each)
    const quadrants = [
      // Top-left: Low cost, High sales
      { x: 0, y: 0, width: halfWidth, height: halfHeight, fill: '#FEF3C7', label: 'Basso Costo\nAlte Vendite' },
      // Top-right: High cost, High sales
      { x: halfWidth, y: 0, width: halfWidth, height: halfHeight, fill: '#D1FAE5', label: 'Alto Costo\nAlte Vendite' },
      // Bottom-left: Low cost, Low sales
      { x: 0, y: halfHeight, width: halfWidth, height: halfHeight, fill: '#FEE2E2', label: 'Basso Costo\nBasse Vendite' },
      // Bottom-right: High cost, Low sales
      { x: halfWidth, y: halfHeight, width: halfWidth, height: halfHeight, fill: '#DBEAFE', label: 'Alto Costo\nBasse Vendite' }
    ];

    quadrants.forEach((quad, i) => {
      // Draw background
      g.append("rect")
        .attr("x", quad.x)
        .attr("y", quad.y)
        .attr("width", quad.width)
        .attr("height", quad.height)
        .attr("fill", quad.fill)
        .attr("opacity", 0.4);

      // Draw border for each quadrant
      g.append("rect")
        .attr("x", quad.x)
        .attr("y", quad.y)
        .attr("width", quad.width)
        .attr("height", quad.height)
        .attr("fill", "none")
        .attr("stroke", "#fff")
        .attr("stroke-width", 10)
        .attr("rx", 4)
        .attr("ry", 4);

      // Add quadrant labels
      const labelLines = quad.label.split('\n');
      const labelX = quad.x + quad.width / 2;
      const labelY = quad.y + 20; // Position near top of quadrant

      labelLines.forEach((line, lineIndex) => {
        g.append("text")
          .attr("x", labelX)
          .attr("y", labelY + lineIndex * 18)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .style("font-size", "13px")
          .style("font-weight", "700")
          .style("fill", "#333")
          .style("pointer-events", "none")
          .style("text-transform", "uppercase")
          .text(line);
      });
    });

    const baseSize = 40;

    // Prepare data for force simulation with initial positions
    const simulationData = data.map(product => ({
      ...product,
      x: getXPosition(product.cost),
      y: getYPosition(product.sales),
      targetX: getXPosition(product.cost),
      targetY: getYPosition(product.sales)
    }));

    // Run force simulation synchronously to completion
    const simulation = d3.forceSimulation(simulationData)
      .force("x", d3.forceX((d: any) => d.targetX).strength(0.8))
      .force("y", d3.forceY((d: any) => d.targetY).strength(0.8))
      .force("collide", d3.forceCollide(baseSize / 2 + 4))
      .stop();

    // Run simulation for a fixed number of ticks to get final positions
    const radius = baseSize / 2;
    for (let i = 0; i < 300; i++) {
      simulation.tick();
      
      // Constrain positions to chart bounds after each tick
      simulationData.forEach(d => {
        d.x = Math.max(radius, Math.min(innerWidth - radius, d.x));
        d.y = Math.max(radius, Math.min(innerHeight - radius, d.y));
      });
    }

    // Axes removed - using debug grid instead

    // Add axis labels
    g.append("text")
      .attr("x", innerWidth / 2)
      .attr("y", innerHeight + 30)
      .attr("text-anchor", "middle")
      .style("font-size", "14px")
      .style("font-weight", "600")
      .style("fill", "#333")
      .text("PREZZO RETAIL");

    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerHeight / 2)
      .attr("y", -30)
      .attr("text-anchor", "middle")
      .style("font-size", "14px")
      .style("font-weight", "600")
      .style("fill", "#333")
      .text("SCORE VENDITA");

    // Create tooltip
    const tooltip = g.append("g")
      .attr("class", "tooltip")
      .style("display", "none");

    const tooltipRect = tooltip.append("rect")
      .attr("fill", "rgba(0, 0, 0, 0.9)")
      .attr("rx", 4)
      .attr("ry", 4);

    const tooltipText = tooltip.append("text")
      .attr("fill", "white")
      .style("font-size", "12px")
      .style("font-weight", "500")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle");

    // Draw products with initial positions
    const productGroups = g.selectAll(".product-group")
      .data(simulationData, (d: any) => d.productId)
      .enter()
      .append("g")
      .attr("class", (d) => `product-group product-${d.productId}`)
      .attr("transform", (d) => `translate(${d.x},${d.y})`)
      .style("cursor", "pointer");

    // Add colored border circle
    productGroups.append("circle")
      .attr("class", "border-circle")
      .attr("r", baseSize / 2 + 3)
      .attr("fill", "white")
      .attr("stroke", (d) => d.color)
      .attr("stroke-width", 3);

    // Add product image or placeholder
    productGroups.each(function(d) {
      const group = d3.select(this);
      
      if (d.imageUrl) {
        group.append("defs")
          .append("clipPath")
          .attr("id", `clip-${d.productId}`)
          .append("circle")
          .attr("r", baseSize / 2);

        group.append("image")
          .attr("class", "product-image")
          .attr("href", d.imageUrl)
          .attr("x", -baseSize / 2)
          .attr("y", -baseSize / 2)
          .attr("width", baseSize)
          .attr("height", baseSize)
          .attr("clip-path", `url(#clip-${d.productId})`)
          .attr("preserveAspectRatio", "xMidYMid slice");
      } else {
        // Placeholder circle
        group.append("circle")
          .attr("class", "placeholder-circle")
          .attr("r", baseSize / 2)
          .attr("fill", "#E5E7EB");

        // Product ID text
        const displayText = d.productId.length > 4 
          ? d.productId.substring(0, 4) 
          : d.productId;
        
        group.append("text")
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .style("font-size", "10px")
          .style("font-weight", "bold")
          .style("fill", "#374151")
          .style("pointer-events", "none")
          .text(displayText);
      }
    });

    // Add interaction handlers
    productGroups
      .on("click", function(event, d) {
        // Copy SKU to clipboard
        navigator.clipboard.writeText(d.productId).then(() => {
          // Show "copied!" message
          tooltipText.text("");
          tooltipText.append("tspan")
            .attr("x", 0)
            .attr("dy", 0)
            .style("font-weight", "600")
            .style("fill", "#EDFEF4")
            .text("SKU copiato!");
          
          // Recalculate tooltip size
          const bbox = (tooltipText.node() as SVGTextElement).getBBox();
          const padding = 8;
          const rectWidth = bbox.width + padding * 2;
          const rectHeight = bbox.height + padding * 2;
          
          tooltipRect
            .attr("x", -rectWidth / 2)
            .attr("y", -rectHeight / 2)
            .attr("width", rectWidth)
            .attr("height", rectHeight);
          
          tooltipText.attr("y", 0);
          
          // Revert back after 1 second
          setTimeout(() => {
            // Restore original tooltip
            tooltipText.text("");
            
            tooltipText.append("tspan")
              .attr("x", 0)
              .attr("dy", 0)
              .style("font-weight", "500")
              .style("fill", "white")
              .text(`SKU: ${d.productId}`);
            
            tooltipText.append("tspan")
              .attr("x", 0)
              .attr("dy", "1.4em")
              .style("font-size", "10px")
              .style("font-style", "italic")
              .style("font-weight", "300")
              .style("fill", "white")
              .text("clicca per copiare lo SKU");
            
            const bbox = (tooltipText.node() as SVGTextElement).getBBox();
            const padding = 8;
            const rectWidth = bbox.width + padding * 2;
            const rectHeight = bbox.height + padding * 2;
            
            tooltipRect
              .attr("x", -rectWidth / 2)
              .attr("y", -rectHeight / 2 - padding)
              .attr("width", rectWidth)
              .attr("height", rectHeight);
            
            tooltipText.attr("y", -bbox.height / 2);
          }, 1000);
        }).catch(err => {
          console.error('Failed to copy SKU:', err);
        });
      })
      .on("mouseover", function(event, d) {
        if (onProductHover) {
          onProductHover(d.productId);
        }

        // Update visual size (using current simulated position)
        const currentTransform = d3.select(this).attr("transform");
        const match = currentTransform.match(/translate\(([^,]+),([^)]+)\)/);
        const currentX = match ? parseFloat(match[1]) : d.x;
        const currentY = match ? parseFloat(match[2]) : d.y;
        
        d3.select(this).select(".border-circle")
          .attr("stroke-width", 4);
        
        d3.select(this)
          .raise()
          .transition()
          .duration(200)
          .attr("transform", `translate(${currentX},${currentY}) scale(1.2)`);

        // Show tooltip
        tooltipText.text("");
        
        // Add SKU
        tooltipText.append("tspan")
          .attr("x", 0)
          .attr("dy", 0)
          .style("font-weight", "500")
          .text(`SKU: ${d.productId}`);
        
        // Add instruction in italic
        tooltipText.append("tspan")
          .attr("x", 0)
          .attr("dy", "1.4em")
          .style("font-size", "10px")
          .style("font-style", "italic")
          .style("font-weight", "300")
          .text("clicca per copiare lo SKU");

        const bbox = (tooltipText.node() as SVGTextElement).getBBox();
        const padding = 8;
        const rectWidth = bbox.width + padding * 2;
        const rectHeight = bbox.height + padding * 2;

        tooltipRect
          .attr("x", -rectWidth / 2)
          .attr("y", -rectHeight / 2 - padding)
          .attr("width", rectWidth)
          .attr("height", rectHeight);

        tooltipText
          .attr("y", -bbox.height / 2);

        // Position tooltip above the product (using current simulated position)
        let xPos = currentX;
        let yPos = currentY - baseSize / 2 - rectHeight - 15;

        // Keep tooltip inside chart horizontally
        const minX = rectWidth / 2 + 10;
        const maxX = innerWidth - rectWidth / 2 - 10;
        
        if (xPos < minX) {
          xPos = minX;
        } else if (xPos > maxX) {
          xPos = maxX;
        }

        // If tooltip would go above the chart, position it below instead
        if (yPos < rectHeight + 10) {
          yPos = currentY + baseSize / 2 + rectHeight + 15;
        }

        tooltip
          .attr("transform", `translate(${xPos},${yPos})`)
          .style("display", null);
      })
      .on("mouseout", function(event, d) {
        if (onProductHover) {
          onProductHover(null);
        }

        // Get current position from transform
        const currentTransform = d3.select(this).attr("transform");
        const match = currentTransform.match(/translate\(([^,]+),([^)]+)\)/);
        const currentX = match ? parseFloat(match[1]) : d.x;
        const currentY = match ? parseFloat(match[2]) : d.y;

        d3.select(this).select(".border-circle")
          .attr("stroke-width", 3);

        tooltip.style("display", "none");

        // Scale back
        d3.select(this)
          .transition()
          .duration(200)
          .attr("transform", `translate(${currentX},${currentY}) scale(1)`);
      });

  }, [data, onProductHover]);

  // Handle external hover state changes without rerunning simulation
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    
    // Update border width based on hover state
    svg.selectAll(".product-group").each(function(d: any) {
      const group = d3.select(this);
      const isHovered = hoveredProductId === d.productId;
      
      group.select(".border-circle")
        .attr("stroke-width", isHovered ? 4 : 3);
    });
  }, [hoveredProductId]);

  return (
    <div ref={containerRef} className="w-full h-full flex justify-center items-start">
      <svg
        ref={svgRef}
        className="bg-white max-w-full max-h-full max-w-[850px]"
        viewBox="0 0 790 700"
        preserveAspectRatio="xMidYMid meet"
      />
    </div>
  );
};

export default QuadrantChart;
